import React, { ReactNode, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Icons,
  Button,
  ToolButton,
} from '../';
import { IconPresentationProvider } from '@ohif/ui-next';
import NavBar from '../NavBar';

interface HeaderProps {
  children?: ReactNode;
  menuOptions: Array<{
    title: string;
    icon?: string;
    onClick: () => void;
  }>;
  isReturnEnabled?: boolean;
  onClickReturnButton?: () => void;
  isSticky?: boolean;
  WhiteLabeling?: {
    createLogoComponentFn?: (React: any, props: any) => ReactNode;
  };
  PatientInfo?: ReactNode;
  Secondary?: ReactNode;
  UndoRedo?: ReactNode;
}

/** Hook: detecta mobile (<= 768px) */
function useIsMobile(query = '(max-width: 768px)') {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(query);

    const handler = (e: MediaQueryListEvent | MediaQueryList) => {
      const matches = 'matches' in e ? e.matches : (e as MediaQueryList).matches;
      setIsMobile(matches);
    };

    // estado inicial
    setIsMobile(mql.matches);

    // compatibilidad navegadores
    if ('addEventListener' in mql) mql.addEventListener('change', handler as any);
    else (mql as any).addListener(handler);

    return () => {
      if ('removeEventListener' in mql) mql.removeEventListener('change', handler as any);
      else (mql as any).removeListener(handler);
    };
  }, [query]);

  return isMobile;
}

const HamburgerIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    className={className}
    width="20"
    height="20"
  >
    <path
      d="M3 6h18M3 12h18M3 18h18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

function Header({
  children,
  menuOptions,
  isReturnEnabled = true,
  onClickReturnButton,
  isSticky = false,
  WhiteLabeling,
  PatientInfo,
  UndoRedo,
  Secondary,
  ...props
}: HeaderProps): ReactNode {
  const isMobile = useIsMobile();
  const mobileRef = useRef<HTMLDivElement | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Ensure menu is hidden by default on mobile, and unaffected on desktop
  useEffect(() => {
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  }, [isMobile]);

  const onClickReturn = () => {
    if (isReturnEnabled && onClickReturnButton) onClickReturnButton();
  };

  return (
    <IconPresentationProvider
      size="small"
      IconContainer={ToolButton}
    >
      {/* Forzamos cero padding/margin/top en mobile desde la raíz */}
      <NavBar
        isSticky={isSticky}
        {...props}
        className={classNames(
          // quita cualquier relleno/margen heredado en mobile
          '!top-0 !m-0 !p-0',
          // permite que desktop mantenga su estilo si NavBar trae padding por defecto
          'md:!p-0'
        )}
      >
        {/* DESKTOP HEADER BAR (oculto en mobile para eliminar el “hueco” azul) */}
        {!isMobile && (
          <div className="relative hidden h-[48px] items-center px-3 md:flex">
            {/* Left: Return + Nombre institución */}
            <div className="flex flex-1 items-center">
              <div
                className={classNames(
                  'mr-3 inline-flex items-center',
                  isReturnEnabled && 'cursor-pointer'
                )}
                onClick={onClickReturn}
                data-cy="return-to-work-list"
              >
                {isReturnEnabled && <Icons.ArrowLeft className="text-primary ml-1 h-7 w-7" />}
                <div className="ml-1 font-semibold tracking-wide text-white">
                  {typeof window !== 'undefined'
                    ? (localStorage.getItem('INSTITUCION') ?? 'DESARROLLO Y MEDICINA')
                    : 'DESARROLLO Y MEDICINA'}
                </div>
              </div>

              {/* Secondary (solo desktop) */}
              <div className="ml-2 hidden h-8 items-center md:flex">{Secondary}</div>
            </div>

            {/* Center: children (solo desktop) */}
            <div className="hidden flex-1 items-center justify-center md:flex">
              <div className="flex items-center justify-center space-x-2">{children}</div>
            </div>

            {/* Right: PatientInfo + Dropdown settings */}
            <div className="flex flex-1 justify-end">
              <div className="hidden select-none items-center md:flex">
                {PatientInfo}
                <div className="border-primary-dark mx-1.5 h-[25px] border-r" />
                <div className="flex-shrink-0">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-primary hover:bg-primary-dark mt-2 h-full w-full"
                      >
                        <Icons.GearSettings />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {menuOptions.map((option, index) => {
                        const IconComponent = option.icon
                          ? Icons[option.icon as keyof typeof Icons]
                          : null;
                        return (
                          <DropdownMenuItem
                            key={index}
                            onSelect={option.onClick}
                            className="flex items-center gap-2 py-2"
                          >
                            {IconComponent && (
                              <span className="flex h-4 w-4 items-center justify-center">
                                <Icons.ByName name={option.icon} />
                              </span>
                            )}
                            <span className="flex-1">{option.title}</span>
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* MOBILE TOGGLE BUTTON (top-right), only on mobile */}
        {isMobile && (
          <button
            type="button"
            aria-label="Toggle menu"
            aria-controls="ohif-mobile-menu"
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen(prev => !prev)}
            className={classNames(
              'border-primary-dark text-primary fixed right-2 top-2 z-[10030] rounded border bg-black/70 p-2 backdrop-blur md:hidden',
              'focus:ring-primary focus:outline-none focus:ring-2'
            )}
          >
            <HamburgerIcon className="h-5 w-5" />
          </button>
        )}

        {/* MOBILE MENU (hidden by default, toggled via button) */}
        {isMobile && mobileMenuOpen && (
          <div
            ref={mobileRef}
            id="ohif-mobile-menu"
            role="region"
            aria-label="Mobile navigation"
            className="!m-0 !p-0 md:hidden"
            style={{
              // blindaje anti-espacios: si NavBar/parent es sticky/relative
              top: 0,
              marginTop: 0,
              paddingTop: 0,
            }}
          >
            {/* NADA de header alto aquí. Arrancamos directamente con el contenido */}
            <div className="flex flex-col gap-2 p-2">
              {/* Secondary (mobile) */}
              {Secondary && (
                <div className="border-primary-dark/10 rounded-md border p-2">{Secondary}</div>
              )}

              {/* Children (mobile) */}
              {children && (
                <div className="border-primary-dark/10 rounded-md border">
                  <div className="flex flex-col gap-2 p-2 sm:flex-row sm:flex-wrap">{children}</div>
                </div>
              )}

              {/* PatientInfo (mobile)
              {PatientInfo}
              */}

              {/* Menú (mobile)
              {menuOptions?.length > 0 && (
                <nav
                  aria-label="Mobile actions"
                  className="border-primary-dark/10 overflow-hidden rounded-md border"
                >
                  {menuOptions.map((option, idx) => {
                    const isLast = idx === menuOptions.length - 1;
                    return (
                      <button
                        key={idx}
                        onClick={option.onClick}
                        className={classNames(
                          'hover:bg-primary-dark/10 flex w-full items-center gap-2 p-3 text-left',
                          !isLast && 'border-primary-dark/10 border-b'
                        )}
                      >
                        {option.icon && (
                          <span className="flex h-4 w-4 items-center justify-center">
                            <Icons.ByName name={option.icon} />
                          </span>
                        )}
                        <span className="flex-1">{option.title}ss</span>
                      </button>
                    );
                  })}
                </nav>
              )}*/}
            </div>
          </div>
        )}
      </NavBar>
    </IconPresentationProvider>
  );
}

export default Header;
